import { forwardRef } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "~/lib/utils";

const inputVariants = cva(
  "flex w-full border bg-white text-[#1c2024] outline-none placeholder:text-[rgba(0,5,29,0.45)] focus:border-[#4c6f90] focus:ring-2 focus:ring-[#4c6f90]/20 disabled:cursor-not-allowed disabled:bg-[#f9f9fb] disabled:text-[#60646c]",
  {
    variants: {
      size: {
        sm: "h-8 px-3 text-sm rounded",
        md: "h-10 px-3 text-sm rounded-md",
        lg: "h-12 px-4 text-base rounded-md",
      },
      variant: {
        default: "border-[rgba(0,9,50,0.12)]",
        error: "border-red-600 focus:border-red-600 focus:ring-red-600/20",
      },
    },
    defaultVariants: {
      size: "md",
      variant: "default",
    },
  }
);

export type InputProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, "size"> &
  VariantProps<typeof inputVariants> & {
    invalid?: boolean;
  };

const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, size, variant, invalid, ...props }, ref) => (
    <input
      ref={ref}
      className={cn(inputVariants({ size, variant: invalid ? "error" : variant, className }))}
      {...props}
    />
  )
);

Input.displayName = "Input";

const InputGroup = forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("relative w-full", className)} {...props} />
  )
);

InputGroup.displayName = "InputGroup";

const InputLeftContent = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("absolute left-0 top-1/2 -translate-y-1/2 pl-3", className)} {...props} />
);

const InputRightContent = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("absolute right-0 top-1/2 -translate-y-1/2 pr-3", className)} {...props} />
);

export { Input, InputGroup, InputLeftContent, InputRightContent };

